import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';

import {
  Box,
  FormControlLabel,
  Typography,
  Checkbox,
  Stack,
  Chip,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  IconButton,
  FormHelperText,
  Autocomplete
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import AddIcon from '@mui/icons-material/Add';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { URL_CONSTANT } from '../../../URLConstants';

import AddNewRouteModal from './AddNewRouteModal';
import { CreateTripData } from '../../../network/Trip';
import { GetAllDeviceList } from '../../../network/Devices';
import { GetRouteList } from '../../../network/Routes';
import { GetVehicleList } from '../../../network/Vehicles';

// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  customDashCard: {
    background: '#FFFFFF',
    boxShadow: '10px 10px 19px 1px #EAEAFF',
    borderRadius: '20px',
    minHeight: '190px'
  },
  root: {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    borderRadius: 3,
    width: 16,
    height: 16,
    boxShadow: 'inset 0px 0px 0px 1px #ccc',
    backgroundColor: 'white',
    border: '1px solid #ccc',
    transition: 'background-color 150ms, border-color 150ms',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  checkedIcon: {
    backgroundColor: '#dddd',
    borderColor: '#ddd',
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      content: '""',
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath%20fill='none'%20stroke='%23ffffff'%20stroke-width='2'%20d='M3.73%208.8l-1.2%201.2%201.1%201.1L7%206.77l4.33%204.33%201.1-1.1-5.42-5.41L3.73%208.8z'/%3E%3C/svg%3E\")",
      transform: 'scale(0.7)',
      transition: 'transform 150ms'
    },
    'input:hover ~ &': {
      backgroundColor: '#ddd'
    }
  },
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', md: '40%' },
  maxWidth: { xs: '70%', md: '40%' },
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1,
  minHeight: '30vh',
  maxHeight: '90vh',
  overflow: 'auto'
};

const CreateTripSchema = Yup.object().shape({
  driverName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
  driverPhoneNo: Yup.string()
    .length(10, 'Phone number must be exactly 10 digits')
    .matches(/^[0-9]{10}$/, 'Phone number is not valid')
    .required('Required'),
  selectedRouteId: Yup.string().required('Required'),
  selectedVehicleId: Yup.string().required('Required'),
  tripName: Yup.string().min(3, 'Too Short!').max(50, 'Too Long!').required('Required'),
  selectedDevices: Yup.array().min(1, 'At least one device must be selected').required('Required')
});

export default function AddSmartLockNewTrip({
  openAssignedModal,
  handleClose,
  onAction,
  alertMetas,
  setAlertMetas
}) {
  const classes = useStyles();
  const navigate = useNavigate();

  const [startDateTime, setStartDateTime] = useState(dayjs(new Date()));
  const [smartLockDeviceListData, setSmartLockDeviceListData] = useState([]);
  const [smartLockRouteList, setSmartLockRouteList] = useState([]);
  const [smartLockVehicleList, setSmartLockVehicleList] = useState([]);
  const [isCreateRoute, setIsCreateRoute] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [error, setError] = useState(false);
  const [routeType, setRouteType] = useState('fixed');

  const [createTripStepsNo, setCreateTripStepsNo] = useState('1');

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250
      }
    }
  };

  const handleRoutetype = (type) => {
    setRouteType(type);
  };

  const createTripFormik = useFormik({
    initialValues: {
      selectedDevices: [],
      selectedRouteId: '',
      selectedVehicleId: '',
      tripNotes: '',
      tripName: '',
      driverName: '',
      driverPhoneNo: '',
      startDateTime: dayjs(new Date())
    },
    validationSchema: CreateTripSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        name: values?.tripName,
        description: values?.tripNotes,
        status: 'scheduled',
        startTime: startDateTime,
        driverName: values?.driverName,
        driverPhone: values?.driverPhoneNo.toString(),
        driverImage: 'https://smartlockfirmwaredata.s3.ap-south-1.amazonaws.com/driverImage1.png',
        vehicleId: values?.selectedVehicleId,
        deviceIds: values.selectedDevices?.map((item) => item?.id),
        routeId: values?.selectedRouteId
      };
      createTrip(payload);
      resetForm();
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = createTripFormik;

  const createTrip = async (data) => {
    try {
      const res = await CreateTripData(data);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'Trip has been created successfully !'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        onAction();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    } finally {
      handleClose();
      // getAllDevices();
      setRouteName('');
      setCreateTripStepsNo('1');
    }
  };

  const getAllDevices = async () => {
    try {
      const res = await GetAllDeviceList();
      if (res) {
        setSmartLockDeviceListData(res?.data?.data);
      }
    } catch (error) {
      console.log('Error fetching devices', error);
    }
  };

  const getAllRoutes = async () => {
    try {
      const res = await GetRouteList();
      if (res) {
        setSmartLockRouteList(res?.data?.data);
      }
    } catch (error) {
      console.log('Error fetching routes', error);
    }
  };

  const getAllVehicles = async () => {
    try {
      const res = await GetVehicleList();
      if (res) {
        setSmartLockVehicleList(res?.data?.data);
      }
    } catch (error) {
      console.log('Error fetching vehicles', error);
    }
  };

  const handleCreateRoute = () => {
    setIsCreateRoute(true);
  };

  const handleSelectedRouteIdChange = (routeId, routeName) => {
    createTripFormik.setFieldValue('selectedRouteId', routeId);
    setRouteName(routeName);
  };

  const onAddNewRouteModalSubmit = (msg, action) => {
    setAlertMetas({
      ...alertMetas,
      severity: action === 'SUCCESS' ? 'success' : 'error',
      snackBarOpened: true,
      msg
    });
    setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
  };

  useEffect(() => {
    getAllDevices();
    getAllRoutes();
    getAllVehicles();
  }, []);

  return (
    <>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="add-trip-modal"
      >
        <Box sx={style}>
          <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 900 }}>Add Trip</Typography>
          <FormikProvider value={createTripFormik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              {(() => {
                if (createTripStepsNo === '1')
                  return (
                    <Box>
                      <Box>
                        {createTripFormik.values.selectedDevices !== null &&
                        createTripFormik.values.selectedDevices !== undefined ? (
                          <FormControl
                            size="small"
                            fullWidth
                            sx={{
                              mb: { xs: 1, sm: 2, md: 3 },
                              mt: { xs: 1, sm: 3, md: 4 }
                            }}
                            error={Boolean(createTripFormik.errors.selectedDevices)}
                          >
                            <Autocomplete
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: { xs: '12px', md: '14px' },
                                  fontWeight: '500'
                                }
                              }}
                              multiple
                              id="device-search"
                              options={smartLockDeviceListData || []}
                              getOptionLabel={(device) => device?.name || ''}
                              filterSelectedOptions
                              value={createTripFormik?.values?.selectedDevices}
                              onChange={(event, newValue) => {
                                createTripFormik.setFieldValue('selectedDevices', newValue);
                                setError(false);
                              }}
                              popupIcon={<ExpandMoreIcon />}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Devices *"
                                  sx={{
                                    '& .MuiInputLabel-root': {
                                      fontSize: { xs: '12px', sm: '14px' }, // Responsive font size for the label
                                      fontWeight: 500
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      fontSize: { xs: '12px', sm: '14px' }, // Responsive input text size
                                      padding: { xs: '14px 8px', sm: '16px' } // Responsive padding
                                    },
                                    '& .MuiFormHelperText-root': {
                                      fontSize: { xs: '10px', sm: '12px' } // Responsive helper text font size
                                    }
                                  }}
                                  placeholder="Search for devices"
                                  error={Boolean(createTripFormik.errors.selectedDevices)}
                                />
                              )}
                              renderTags={(selected, getTagProps) =>
                                selected.map((option, index) => (
                                  <Chip
                                    key={index}
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    color="primary"
                                    sx={{
                                      fontSize: { xs: '12px', md: '14px' },
                                      '& .MuiChip-deleteIcon': {
                                        fontSize: { xs: '16px', sm: '16px', md: '18px' }
                                      }
                                    }}
                                  />
                                ))
                              }
                              renderOption={(props, option) => (
                                <MenuItem
                                  key={option?.id}
                                  value={option}
                                  {...props}
                                  className={classes.menuItem}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ width: '100%' }}
                                  >
                                    {option?.name}
                                    {createTripFormik.values.selectedDevices?.includes(option) ? (
                                      <IconButton aria-label="remove" color="error">
                                        <RemoveOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    ) : (
                                      <IconButton aria-label="add" color="success">
                                        <AddOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Stack>
                                </MenuItem>
                              )}
                            />
                            {createTripFormik.touched.selectedDevices &&
                              createTripFormik.errors.selectedDevices && (
                                <FormHelperText>
                                  {createTripFormik.errors.selectedDevices}
                                </FormHelperText>
                              )}
                          </FormControl>
                        ) : null}
                      </Box>

                      <Stack
                        mt={2}
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Button variant="outlined" size="small" onClick={handleClose}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setCreateTripStepsNo('2')}
                          disabled={Boolean(
                            createTripFormik.errors.selectedDevices ||
                              createTripFormik.values.selectedDevices.length === 0
                          )}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Box>
                  );

                if (createTripStepsNo === '2')
                  return (
                    <Box>
                      <Box>
                        <Stack mt={2} direction="row" alignItems="center" spacing={1}>
                          <FormControl fullWidth>
                            <FormControlLabel
                              sx={{
                                border: '1px solid',
                                borderRadius: '10px',
                                p: { xs: 0, sm: 1 },
                                ml: { xs: 0, sm: 1 },
                                mr: { xs: 0, sm: 1 },
                                typography: { fontSize: { xs: '12px', sm: '14px' } }
                              }}
                              checked={routeType === 'fixed'}
                              onChange={() => handleRoutetype('fixed')}
                              control={<Checkbox size="small" />}
                              label={
                                <Typography
                                  sx={{
                                    fontSize: { xs: '12px', sm: '16px' },
                                    fontWeight: 500
                                  }}
                                >
                                  Fixed
                                </Typography>
                              }
                            />
                          </FormControl>
                          <FormControl fullWidth>
                            <FormControlLabel
                              sx={{
                                border: '1px solid',
                                borderRadius: '10px',
                                p: { xs: 0, sm: 1 },
                                pr: { xs: 4 },
                                ml: { xs: 0, sm: 1 },
                                mr: { xs: 0, sm: 1 },
                                typography: { fontSize: { xs: '12px', sm: '14px' } }
                              }}
                              checked={routeType === 'adhoc'}
                              onChange={() => handleRoutetype('adhoc')}
                              control={<Checkbox size="small" />}
                              label={
                                <Typography
                                  sx={{
                                    fontSize: { xs: '12px', sm: '16px' },
                                    fontWeight: 500
                                  }}
                                >
                                  AdHoc
                                </Typography>
                              }
                            />
                          </FormControl>
                        </Stack>
                      </Box>

                      {routeType === 'fixed' ? (
                        <Box>
                          <FormControl
                            fullWidth
                            sx={{ mb: 2, mt: 2 }}
                            error={Boolean(touched.selectedRouteId && errors.selectedRouteId)}
                          >
                            <InputLabel
                              id="route-select-label"
                              sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                            >
                              Select Route *
                            </InputLabel>
                            <Select
                              labelId="route-select-label"
                              sx={{
                                fontSize: { xs: '12px', sm: '14px' },
                                '& .MuiOutlinedInput-input': {
                                  padding: { xs: '14px 8px', sm: '16.5px 14px' }
                                }
                              }}
                              label="Select Route *"
                              id="route_select_id"
                              IconComponent={ExpandMoreIcon}
                              {...getFieldProps('selectedRouteId')}
                              error={Boolean(touched.selectedRouteId && errors.selectedRouteId)}
                              helpertext={touched.selectedRouteId && errors.selectedRouteId}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 180,
                                    overflowY: 'auto'
                                  }
                                }
                              }}
                            >
                              {smartLockRouteList?.length === 0 ? (
                                <MenuItem
                                  className={classes.menuItem}
                                  onClick={() => navigate(URL_CONSTANT.ROUTES)}
                                >
                                  <Typography
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      margin: '0 auto',
                                      fontSize: { xs: '12px', sm: '14px' }
                                    }}
                                  >
                                    Create Route <AddIcon fontSize="small" />
                                  </Typography>
                                </MenuItem>
                              ) : (
                                smartLockRouteList?.map((route, index) => (
                                  <MenuItem
                                    className={classes.menuItem}
                                    value={route?.id}
                                    key={route?.id}
                                    sx={{ textTransform: 'capitalize' }}
                                  >
                                    {route?.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                            {touched.selectedRouteId && errors.selectedRouteId && (
                              <FormHelperText>{errors.selectedRouteId}</FormHelperText>
                            )}
                          </FormControl>
                        </Box>
                      ) : null}

                      {routeType === 'adhoc' ? (
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={handleCreateRoute}
                          sx={{ mb: 2, mt: 2 }}
                        >
                          {routeName || 'Create New Route'}
                        </Button>
                      ) : null}
                      <Box>
                        <FormControl
                          fullWidth
                          sx={{ mb: 2 }}
                          error={Boolean(touched.selectedVehicleId && errors.selectedVehicleId)}
                        >
                          <InputLabel
                            id="vehicle-select-label"
                            sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                          >
                            Select Vehicle *
                          </InputLabel>
                          <Select
                            labelId="vehicle-select-label"
                            sx={{
                              fontSize: { xs: '12px', sm: '14px' },
                              '& .MuiOutlinedInput-input': {
                                padding: { xs: '14px 8px', sm: '16.5px 14px' }
                              }
                            }}
                            label="Select Vehicle *"
                            id="vehicle_select_id"
                            IconComponent={ExpandMoreIcon}
                            {...getFieldProps('selectedVehicleId')}
                            error={Boolean(touched.selectedVehicleId && errors.selectedVehicleId)}
                            helpertext={touched.selectedVehicleId && errors.selectedVehicleId}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 180,
                                  overflowY: 'auto'
                                }
                              }
                            }}
                          >
                            {smartLockVehicleList?.length === 0 ? (
                              <MenuItem
                                className={classes.menuItem}
                                onClick={() => navigate(URL_CONSTANT.VEHICLE)}
                              >
                                <Typography
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    margin: '0 auto',
                                    fontSize: { xs: '12px', sm: '14px' }
                                  }}
                                >
                                  Create Vehicle <AddIcon fontSize="small" />
                                </Typography>
                              </MenuItem>
                            ) : (
                              smartLockVehicleList?.map((vehicle) => (
                                <MenuItem
                                  className={classes.menuItem}
                                  value={vehicle?.id}
                                  key={vehicle?.id}
                                  sx={{ textTransform: 'capitalize' }}
                                >
                                  {vehicle?.name}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          {touched.selectedVehicleId && errors.selectedVehicleId && (
                            <FormHelperText>{errors.selectedVehicleId}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>

                      <Stack
                        mb={2}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <FormControl fullWidth>
                          <TextField
                            label={
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: '12px',
                                    md: '14px'
                                  },
                                  fontWeight: 500
                                }}
                              >
                                Driver Name *
                              </Typography>
                            }
                            id="driverName"
                            name="driverName"
                            variant="outlined"
                            {...getFieldProps('driverName')}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                fontSize: { xs: '12px', sm: '14px' },
                                padding: { xs: '14px 8px', sm: '16px' }
                              }
                            }}
                            error={Boolean(touched.driverName && errors.driverName)}
                            helperText={touched.driverName && errors.driverName}
                          />
                        </FormControl>
                        <FormControl fullWidth>
                          <TextField
                            label={
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: '12px',
                                    sm: '14px'
                                  },
                                  fontWeight: 500
                                }}
                              >
                                Driver Phone *
                              </Typography>
                            }
                            id="driverPhoneNo"
                            name="driverPhoneNo"
                            variant="outlined"
                            type="number"
                            {...getFieldProps('driverPhoneNo')}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                fontSize: { xs: '12px', sm: '14px' },
                                padding: { xs: '14px 8px', sm: '16.5px' }
                              }
                            }}
                            error={Boolean(touched.driverPhoneNo && errors.driverPhoneNo)}
                            helperText={touched.driverPhoneNo && errors.driverPhoneNo}
                          />
                        </FormControl>
                      </Stack>

                      <Box>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                              renderInput={(props) => <TextField {...props} />}
                              label={
                                <Typography
                                  sx={{
                                    fontSize: {
                                      xs: '12px',
                                      sm: '14px'
                                    },
                                    fontWeight: 500
                                  }}
                                >
                                  Start Date & Time
                                </Typography>
                              }
                              value={startDateTime}
                              sx={{
                                '& .MuiOutlinedInput-input': {
                                  fontSize: { xs: '12px', sm: '14px' },
                                  padding: { xs: '14px 8px', sm: '16.5px' }
                                }
                              }}
                              onChange={(newValue) => {
                                setStartDateTime(newValue);
                              }}
                            />
                          </LocalizationProvider>
                        </FormControl>
                      </Box>

                      <Stack
                        mt={2}
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setCreateTripStepsNo('1')}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setCreateTripStepsNo('3')}
                          disabled={Boolean(
                            !createTripFormik.values.selectedRouteId ||
                              !createTripFormik.values.selectedVehicleId ||
                              !createTripFormik.values.driverName ||
                              !createTripFormik.values.driverPhoneNo ||
                              errors.selectedRouteId ||
                              errors.selectedVehicleId ||
                              errors.driverName ||
                              errors.driverPhoneNo
                          )}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Box>
                  );
                if (createTripStepsNo === '3')
                  return (
                    <Box>
                      <Box>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                          <TextField
                            label={
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: '12px',
                                    sm: '14px'
                                  },
                                  fontWeight: 500
                                }}
                              >
                                Trip Name *
                              </Typography>
                            }
                            name="tripName"
                            variant="outlined"
                            id="tripName"
                            data-testid="trip-name"
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                fontSize: { xs: '12px', sm: '14px' },
                                padding: { xs: '14px 8px', sm: '16.5px' }
                              }
                            }}
                            {...getFieldProps('tripName')}
                            error={Boolean(touched.tripName && errors.tripName)}
                            helperText={touched.tripName && errors.tripName}
                          />
                        </FormControl>
                      </Box>
                      <Box>
                        <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
                          <TextField
                            label={
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: '12px',
                                    sm: '14px'
                                  },
                                  fontWeight: 500
                                }}
                              >
                                Notes
                              </Typography>
                            }
                            name="tripName"
                            variant="outlined"
                            id="tripName"
                            data-testid="tripNotes"
                            multiline
                            minRows={4}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                fontSize: { xs: '12px', sm: '14px' },
                                padding: { xs: '14px 8px', sm: '16px' }
                              }
                            }}
                            {...getFieldProps('tripNotes')}
                            error={Boolean(touched.tripNotes && errors.tripNotes)}
                            helpertext={touched.tripNotes && errors.tripNotes}
                          />
                        </FormControl>
                      </Box>

                      <Stack
                        mt={2}
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => setCreateTripStepsNo('2')}
                          size="small"
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          size="small"
                          disabled={Boolean(!createTripFormik?.values?.tripName || errors.tripName)}
                        >
                          Create Trip
                        </Button>
                      </Stack>
                    </Box>
                  );
              })()}
            </Form>
          </FormikProvider>
        </Box>
      </Modal>

      {isCreateRoute && (
        <AddNewRouteModal
          openAssignedModal={isCreateRoute}
          setIsAddRouteModalOpen={setIsCreateRoute}
          onAction={(msg, action) => onAddNewRouteModalSubmit(msg, action)}
          adhocRouteData={handleSelectedRouteIdChange}
        />
      )}
    </>
  );
}
